import { type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FilterFieldList } from "../../../components/UIToolkit/Filters/FilterFieldList";
import { FilterSidebar } from "../../../components/UIToolkit/Filters/FilterSidebar";
import type { FilterSection } from "../../../components/UIToolkit/Filters/filterFields";
import { usePbjNetwork } from "./PbjNetworkContext";
import { BUILDING_FACET } from "./useBuildingGridParts";

/**
 * The shared Building filter on its own, for a network surface that isn't a row grid (the Summary
 * tab, the Hours by Role chart). Same group, same URL-backed selection as the grids' Building facet.
 */
export const BuildingFilterRail: FC<{ dataTestId: string }> = ({ dataTestId }) => {
	const { t } = useTranslation();
	const { buildings, selectedBuildingIds, setSelectedBuildingIds } = usePbjNetwork();
	const [collapsed, setCollapsed] = useState(false);

	const sections = useMemo<FilterSection[]>(
		() => [
			{
				id: BUILDING_FACET,
				fields: [
					{
						kind: "facet",
						id: BUILDING_FACET,
						title: t("pbj_model.network.building"),
						options: buildings.map((building) => ({
							id: building.departmentId,
							label: building.name,
							checked: selectedBuildingIds.has(building.departmentId),
						})),
						onToggle: (departmentId: string, checked: boolean) => {
							const next = new Set(selectedBuildingIds);
							if (checked) next.add(departmentId);
							else next.delete(departmentId);
							setSelectedBuildingIds(next);
						},
						searchable: true,
						dataTestId: `${dataTestId}-${BUILDING_FACET}`,
					},
				],
			},
		],
		[t, buildings, selectedBuildingIds, setSelectedBuildingIds, dataTestId],
	);

	return (
		<FilterSidebar
			collapsed={collapsed}
			onCollapsedChange={setCollapsed}
			collapsedVariant="rail"
			frame="flush"
			activeFilterCount={selectedBuildingIds.size > 0 ? 1 : 0}
			onClearAll={() => setSelectedBuildingIds(new Set())}
			dataTestId={`${dataTestId}-sidebar`}
		>
			<FilterFieldList sections={sections} dividers dataTestId={dataTestId} />
		</FilterSidebar>
	);
};
